import React, { useState } from 'react';
import { UserCog, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/app/providers';
import { Button, Card, CardBody } from '@/components/ui';

const ImpersonationPanel: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, impersonateUser, isImpersonating } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isAdmin || isImpersonating) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    try {
      await impersonateUser(email.trim());
      setEmail('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mb-6 border-warning/30 bg-warning/5">
      <CardBody className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-warning/15 text-warning flex-shrink-0">
            <UserCog className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground">{t('impersonation.panelTitle')}</h3>
            <p className="text-sm text-foreground-secondary mt-1 mb-3">
              {t('impersonation.panelDescription')}
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('impersonation.emailPlaceholder')}
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-warning/50 focus:border-warning bg-surface text-foreground text-sm"
                disabled={isLoading}
                required
              />
              <Button
                type="submit"
                variant="secondary"
                size="md"
                disabled={isLoading || !email.trim()}
                leftIcon={isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className="w-4 h-4" />}
                className="whitespace-nowrap"
              >
                {isLoading ? t('impersonation.connecting') : t('impersonation.connect')}
              </Button>
            </form>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default ImpersonationPanel;
